import type { RecipeDto } from '@iac/contracts';
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Badge, BadgeList } from '../../components/Badge';
import { Checklist } from '../../components/Checklist';
import { IconChevronLeft, IconPencilSquare } from '../../components/icons';
import { PageHeader } from '../../components/PageHeader';
import { Panel } from '../../components/Panel';
import { useDict } from '../../i18n/dictionary';
import { usePageTitle } from '../../lib/page';
import { useWakeLock } from '../../lib/wakeLock';
import { NotFound } from '../../routes/NotFound';
import { useRecipes } from './api';
import { recipesDict } from './i18n';
import { RecipeMeta } from './RecipeMeta';

/** The recipe the URL names, out of the loaded list; `null` when it is not there. */
export function useRecipeFromUrl(): RecipeDto | null | undefined {
  const { id } = useParams();
  const { data: recipes } = useRecipes();
  if (!recipes) return undefined;
  return recipes.find((r) => r.id === id) ?? null;
}

/** The page read at the stove. */
export function RecipeView() {
  const recipe = useRecipeFromUrl();
  if (recipe === undefined) return null;
  if (recipe === null) return <NotFound />;
  // Keyed, so moving to another recipe starts with nothing ticked.
  return <Cooking key={recipe.id} recipe={recipe} />;
}

/** Ticks live here and nowhere else: leaving the page forgets them. */
function useTicks(count: number) {
  const [ticks, setTicks] = useState(() => Array<boolean>(count).fill(false));
  const toggle = (index: number) =>
    setTicks((t) => t.map((on, i) => (i === index ? !on : on)));
  return [ticks, toggle] as const;
}

function Cooking({ recipe }: { recipe: RecipeDto }) {
  const t = useDict(recipesDict);
  usePageTitle(`${recipe.name} — ${t.titleSuffix}`);
  useWakeLock(true);
  const [ingredientTicks, toggleIngredient] = useTicks(
    recipe.ingredients.length,
  );
  const [stepTicks, toggleStep] = useTicks(recipe.steps.length);

  return (
    <>
      <Link to="/recipes" className="text-link recipe-back">
        <IconChevronLeft />
        {t.back}
      </Link>
      <PageHeader
        title={recipe.name}
        actions={
          <Link
            to={`/recipes/${recipe.id}/edit`}
            className="btn-icon-ghost"
            aria-label={t.edit}
            title={t.edit}
          >
            <IconPencilSquare />
          </Link>
        }
      />
      {(recipe.servings !== null ||
        recipe.minutes !== null ||
        recipe.tags.length > 0) && (
        <div className="recipe-summary">
          <RecipeMeta recipe={recipe} />
          {recipe.tags.length > 0 && (
            <BadgeList>
              {recipe.tags.map((tag) => (
                <Badge key={tag} tone="neutral">
                  {tag}
                </Badge>
              ))}
            </BadgeList>
          )}
        </div>
      )}

      {recipe.ingredients.length > 0 && (
        <Panel title={t.ingredients}>
          <Checklist
            items={recipe.ingredients}
            checked={ingredientTicks}
            onToggle={toggleIngredient}
          />
        </Panel>
      )}
      {recipe.steps.length > 0 && (
        <Panel title={t.steps}>
          <Checklist
            items={recipe.steps}
            checked={stepTicks}
            onToggle={toggleStep}
            numbered
          />
        </Panel>
      )}
      {recipe.notes !== null && (
        <Panel title={t.notes}>
          <p className="recipe-notes">{recipe.notes}</p>
        </Panel>
      )}
    </>
  );
}
